import { useState, useEffect, useCallback } from 'react';
import { X, ArrowLeft, Plus, Trash2, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { OutsourcedShow, OutsourcedShowDTO, Promo } from '../types/OutsourcedShow';
import { outsourcedShowService } from '../services/outsourcedShowService';
import { fetchAvailableGenres, fetchAvailableTropes, Genre, Trope } from '../services/cmsService';
import CountrySelect from '../constants/CountrySelect';
import { toast } from 'sonner';
import {CharacterGender} from "../constants/constants";
import { ShowMetaDataDTO, ShowMetaDataType } from '../types/Show';
import { useShowMetadata } from '../hooks/useShowMetadata';
import { ThumbnailUploader } from './ThumbnailUploader';
import { getThumbnailDimensions, getThumbnailDisplayName } from '../utils/thumbnailUtils';

// Debounce utility function
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

interface EditShowDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  show: OutsourcedShow | null;
  onSave: (updatedShow: OutsourcedShow) => void;
}

export function EditShowDrawer({ isOpen, onClose, show, onSave }: EditShowDrawerProps) {
  const [formData, setFormData] = useState<Partial<OutsourcedShowDTO>>({});
  const [loading, setLoading] = useState(false);
  const [availableGenres, setAvailableGenres] = useState<Genre[]>([]);
  const [availableTropes, setAvailableTropes] = useState<Trope[]>([]);
  
  // Promo state
  const [newPromoLevel, setNewPromoLevel] = useState<Promo>({
    level: '',
    writerName: '',
    producerName: '',
    promosTested: [],
    bestCpi: 0,
    percentMaleInCpi: 0,
    bestCpt: 0,
    percentMaleInCpt: 0,
    promosWithCpiLessThan2: [],
    promosWithCptLessThan50: [],
    deleted: false,
  });
  const [currentPromoTestedLink, setCurrentPromoTestedLink] = useState('');
  const [currentCpiLink, setCurrentCpiLink] = useState('');
  const [currentCptLink, setCurrentCptLink] = useState('');
  const [currentSuccessfulPromoLink, setCurrentSuccessfulPromoLink] = useState('');
  const [isEditingPromo, setIsEditingPromo] = useState(false);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);

  // Show metadata handling with auto-save
  const { metadata, updateMetadata, isAutoSaving } = useShowMetadata({
    showId: show?.id,
    onMetadataChange: (newMetadata) => {
      // Handle metadata changes if needed
      console.log('Metadata updated:', newMetadata);
    },
    onMetadataAutoSave: async (newMetadata) => {
      if (show?.id) {
        // Here you would call your API to save metadata
        // For now, we'll simulate the auto-save
        console.log('Auto-saving metadata for show:', show.id, newMetadata);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // In a real implementation, you'd call something like:
        // await showMetadataService.updateShowMetadata(show.id, newMetadata);
        
        // Update the form data to include metadata if needed
        const updatedFormData = { ...formData, metadata: newMetadata };
        if (show) {
          await autoSaveChanges(updatedFormData);
        }
      }
    },
    autoSaveDelay: 1000 // 1 second delay for auto-save
  });

  // Fetch genres and tropes when drawer opens
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [genres, tropes] = await Promise.all([
            fetchAvailableGenres(),
            fetchAvailableTropes()
          ]);
          if (genres && genres.length > 0) {
            setAvailableGenres(genres);
          }
          if (tropes && tropes.length > 0) {
            setAvailableTropes(tropes);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  // Initialize form data when show changes
  useEffect(() => {
    if (show) {
      // Edit mode - populate with existing show data
      setFormData({
        showId: show.showId,
        ogShowName: show.ogShowName,
        episodes: show.episodes,
        source: show.source || '',
        genre: show.genre,
        trope: show.trope,
        leadCharacterGender: show.leadCharacterGender,
        originCountry: show.originCountry || '',
        dashreelsGrowthName: show.dashreelsGrowthName || '',
        subGenre: show.subGenre || '',
        tierAsPerPartner: show.tierAsPerPartner || '',
        tierAsPerBenchmarking: show.tierAsPerBenchmarking || '',
        isSelected: show.isSelected,
        rationaleForSelection: show.rationaleForSelection || '',
        showLink: show.showLink || '',
        scrapingLink: show.scrapingLink || '',
        licensingSource: show.licensingSource || '',
        successfulPromoLinks: show.successfulPromoLinks || [],
        promos: show.promos || []
      });
    } else {
      // Create mode - initialize empty form
      setFormData({
        showId: '',
        ogShowName: '',
        episodes: 0,
        source: '',
        genre: '',
        trope: '',
        leadCharacterGender: '',
        originCountry: '',
        dashreelsGrowthName: '',
        subGenre: '',
        tierAsPerPartner: '',
        tierAsPerBenchmarking: '',
        isSelected: false,
        rationaleForSelection: '',
        showLink: '',
        scrapingLink: '',
        licensingSource: '',
        successfulPromoLinks: [],
        promos: []
      });
    }
  }, [show]);

  const handleInputChange = (field: keyof OutsourcedShowDTO, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Auto-save after a short delay
    if (show) {
      debounceAutoSave(newFormData);
    }
  };

  // Debounced auto-save function
  const debounceAutoSave = useCallback(
    debounce(async (data: Partial<OutsourcedShowDTO>) => {
      if (!show || loading) return;
      
      // Don't save if required fields are empty
      if (!data.ogShowName?.trim() || !data.episodes || data.episodes <= 0) {
        return;
      }

      try {
        setLoading(true);
        const updatedShow = await outsourcedShowService.updateOutsourcedShow(show.id, data);
        onSave(updatedShow);
        toast.success('Changes saved automatically', { duration: 2000 });
      } catch (error) {
        console.error('Auto-save error:', error);
        toast.error('Failed to save changes');
      } finally {
        setLoading(false);
      }
    }, 1000), // 1 second delay
    [show, loading, onSave]
  );

  // Auto-save function for immediate updates (only for existing shows)
  const autoSaveChanges = async (newFormData: Partial<OutsourcedShowDTO>) => {
    if (!show || loading) return; // Don't auto-save for new shows

    try {
      setLoading(true);
      const updatedShow = await outsourcedShowService.updateOutsourcedShow(show.id, newFormData);
      onSave(updatedShow);
      toast.success('Changes saved automatically', { duration: 2000 });
    } catch (error) {
      console.error('Auto-save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  // Promo management functions
  const handleAddSuccessfulPromoLink = async () => {
    if (currentSuccessfulPromoLink.trim()) {
      const newFormData = {
        ...formData,
        successfulPromoLinks: [...(formData.successfulPromoLinks || []), currentSuccessfulPromoLink.trim()]
      };
      setFormData(newFormData);
      setCurrentSuccessfulPromoLink('');
      await autoSaveChanges(newFormData);
    }
  };

  const handleRemoveSuccessfulPromoLink = async (index: number) => {
    const newFormData = {
      ...formData,
      successfulPromoLinks: (formData.successfulPromoLinks || []).filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    await autoSaveChanges(newFormData);
  };

  const handleAddPromoLink = (field: 'promosTested' | 'promosWithCpiLessThan2' | 'promosWithCptLessThan50', value: string) => {
    if (value.trim()) {
      setNewPromoLevel(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleRemovePromoLink = (field: 'promosTested' | 'promosWithCpiLessThan2' | 'promosWithCptLessThan50', index: number) => {
    setNewPromoLevel(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleAddPromoLevel = async () => {
    if (newPromoLevel.level.trim() && newPromoLevel.writerName.trim()) {
      let newFormData;
      
      if (isEditingPromo && editingPromoId) {
        // Update existing promo
        newFormData = {
          ...formData,
          promos: (formData.promos || []).map(promo => 
            promo.id === editingPromoId 
              ? { ...newPromoLevel, id: editingPromoId }
              : promo
          ),
        };
      } else {
        // Add new promo
        newFormData = {
          ...formData,
          promos: [...(formData.promos || []), { ...newPromoLevel, id: undefined }],
        };
      }
      
      setFormData(newFormData);
      
      // Reset form
      setNewPromoLevel({
        level: '',
        writerName: '',
        producerName: '',
        promosTested: [],
        bestCpi: 0,
        percentMaleInCpi: 0,
        bestCpt: 0,
        percentMaleInCpt: 0,
        promosWithCpiLessThan2: [],
        promosWithCptLessThan50: [],
        deleted: false,
      });
      setIsEditingPromo(false);
      setEditingPromoId(null);
      
      // Auto-save the changes
      await autoSaveChanges(newFormData);
    }
  };

  const handleEditPromo = (promo: Promo) => {
    setNewPromoLevel(promo);
    setIsEditingPromo(true);
    setEditingPromoId(promo.id || null);
  };

  const handleDeletePromo = async (promoId: string | undefined, index: number) => {
    let newFormData;
    
    if (promoId) {
      // Existing promo - soft delete
      newFormData = {
        ...formData,
        promos: (formData.promos || []).map(promo => 
          promo.id === promoId ? { ...promo, deleted: true } : promo
        ),
      };
    } else {
      // New promo - remove completely
      newFormData = {
        ...formData,
        promos: (formData.promos || []).filter((_, i) => i !== index),
      };
    }
    
    setFormData(newFormData);
    await autoSaveChanges(newFormData);
  };

  const handleSave = async () => {
    if (!formData.ogShowName?.trim()) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      if (show) {
        // Update existing show
        const updatedShow = await outsourcedShowService.updateOutsourcedShow(
          show.id, 
          formData as Partial<OutsourcedShowDTO>
        );
        toast.success('Show updated successfully');
        onSave(updatedShow);
      } else {
        // Create new show
        const newShow = await outsourcedShowService.createOutsourcedShow(
          formData as OutsourcedShowDTO
        );
        toast.success('Show created successfully');
        onSave(newShow);
        onClose();
      }
    } catch (error) {
      console.error('Error saving show:', error);
      toast.error(show ? 'Failed to update show' : 'Failed to create show');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.ogShowName?.trim() && 
                     formData.episodes && formData.episodes > 0 && 
                     formData.genre?.trim() && 
                     formData.trope?.trim() && 
                     formData.leadCharacterGender?.trim();

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`fixed top-0 right-0 h-full w-[500px] bg-background border-l shadow-lg z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold">{show ? 'Edit Show' : 'Add New Show'}</h2>
              <p className="text-sm text-muted-foreground">{show ? 'Update show information' : 'Create a new outsourced show'}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full max-h-full">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="showName">OG Name *</Label>
                  <Input
                    id="showName"
                    value={formData.ogShowName || ''}
                    onChange={(e) => handleInputChange('ogShowName', e.target.value)}
                    placeholder="Enter show name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="episodes">Episodes *</Label>
                    <Input
                      id="episodes"
                      type="number"
                      value={formData.episodes || ''}
                      onChange={(e) => handleInputChange('episodes', parseInt(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="source">Source</Label>
                    <Input
                      id="source"
                      value={formData.source || ''}
                      onChange={(e) => handleInputChange('source', e.target.value)}
                      placeholder="Netflix, YouTube, etc."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dashreelsGrowthName">Dashreels Growth Name</Label>
                  <Input
                    id="dashreelsGrowthName"
                    value={formData.dashreelsGrowthName || ''}
                    onChange={(e) => handleInputChange('dashreelsGrowthName', e.target.value)}
                    placeholder="Enter dashreels growth name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subGenre">Sub Genre</Label>
                  <Input
                    id="subGenre"
                    value={formData.subGenre || ''}
                    onChange={(e) => handleInputChange('subGenre', e.target.value)}
                    placeholder="Enter sub genre"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="genre">Genre *</Label>
                  <select
                    id="genre"
                    value={formData.genre || ''}
                    onChange={(e) => handleInputChange('genre', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select genre</option>
                    {availableGenres.map((genre) => (
                      <option key={genre.id} value={genre.name}>{genre.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trope">Trope *</Label>
                  <select
                    id="trope"
                    value={formData.trope || ''}
                    onChange={(e) => handleInputChange('trope', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select trope</option>
                    {availableTropes.map((trope) => (
                      <option key={trope.id} value={trope.name}>{trope.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="leadCharacter">Lead Character Gender *</Label>
                  <select
                    id="leadCharacter"
                    value={formData.leadCharacterGender || ''}
                    onChange={(e) => handleInputChange('leadCharacterGender', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {Object.values(CharacterGender).map(gender => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Evaluation & Sourcing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evaluation & Sourcing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="originCountry">Origin Country</Label>
                  <CountrySelect
                    value={formData.originCountry || ''}
                    onChange={(country) => handleInputChange('originCountry', country)}
                    placeholder="Select country of origin"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="partnerTier">Partner Tier</Label>
                    <Input
                        id="partnerTier"
                        value={formData.tierAsPerPartner || ''}
                        onChange={(e) => handleInputChange('tierAsPerPartner', e.target.value)}
                        placeholder="Partner Tier"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="benchmarkTier">Benchmark Tier</Label>
                    <Input
                        id="benchmarkTier"
                        value={formData.tierAsPerBenchmarking || ''}
                        onChange={(e) => handleInputChange('tierAsPerBenchmarking', e.target.value)}
                        placeholder="Partner Tier"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isSelected"
                    checked={formData.isSelected || false}
                    onChange={(e) => handleInputChange('isSelected', e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <Label htmlFor="isSelected">Is Selected</Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rationaleForSelection">Rationale for Selection</Label>
                  <textarea
                    id="rationaleForSelection"
                    value={formData.rationaleForSelection || ''}
                    onChange={(e) => handleInputChange('rationaleForSelection', e.target.value)}
                    placeholder="Enter rationale for selection"
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="showLink">Show Link</Label>
                  <Input
                    id="showLink"
                    type="url"
                    value={formData.showLink || ''}
                    onChange={(e) => handleInputChange('showLink', e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scrapingLink">Scraping Link</Label>
                  <Input
                    id="scrapingLink"
                    type="url"
                    value={formData.scrapingLink || ''}
                    onChange={(e) => handleInputChange('scrapingLink', e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="licensingSource">Licensing Source</Label>
                  <Input
                    id="licensingSource"
                    value={formData.licensingSource || ''}
                    onChange={(e) => handleInputChange('licensingSource', e.target.value)}
                    placeholder="Enter licensing source"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Promos Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Promo Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="space-y-2">
                  
                  {/* Successful Promo Links */}
                  <AccordionItem value="successful-promos">
                    <AccordionTrigger>
                      Successful Promo Links ({(formData.successfulPromoLinks || []).length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4">
                        {/* Add new successful promo link */}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter successful promo link"
                            value={currentSuccessfulPromoLink}
                            onChange={(e) => setCurrentSuccessfulPromoLink(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddSuccessfulPromoLink();
                              }
                            }}
                            className="flex-1"
                          />
                          <Button 
                            onClick={handleAddSuccessfulPromoLink}
                            disabled={!currentSuccessfulPromoLink.trim()}
                            size="sm"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Display existing successful promo links */}
                        {(formData.successfulPromoLinks || []).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {(formData.successfulPromoLinks || []).map((link, index) => (
                              <Badge 
                                key={index} 
                                variant="secondary" 
                                className="flex items-center gap-1 pr-1 max-w-[200px]"
                              >
                                <span className="truncate" title={link}>
                                  {link.length > 30 ? `${link.substring(0, 30)}...` : link}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveSuccessfulPromoLink(index)}
                                  className="h-4 w-4 p-0 ml-1"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Promo Levels */}
                  <AccordionItem value="promo-levels">
                    <AccordionTrigger>
                      Promo Levels ({(formData.promos || []).filter(p => !p.deleted).length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-6">
                        
                        {/* Add New Promo Form */}
                        <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 space-y-4">
                          <h4 className="font-medium text-sm">
                            {isEditingPromo ? 'Edit Promo Level' : 'Add New Promo Level'}
                          </h4>
                          
                          {/* Basic Info */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="promoLevel">Level *</Label>
                              <Input
                                id="promoLevel"
                                value={newPromoLevel.level}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, level: e.target.value}))}
                                placeholder="Enter level"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="writerName">Writer Name *</Label>
                              <Input
                                id="writerName"
                                value={newPromoLevel.writerName}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, writerName: e.target.value}))}
                                placeholder="Enter writer name"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="producerName">Producer Name</Label>
                            <Input
                              id="producerName"
                              value={newPromoLevel.producerName}
                              onChange={(e) => setNewPromoLevel(prev => ({...prev, producerName: e.target.value}))}
                              placeholder="Enter producer name (optional)"
                            />
                          </div>

                          {/* Metrics */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="bestCpi">Best CPI</Label>
                              <Input
                                id="bestCpi"
                                type="number"
                                step="0.01"
                                value={newPromoLevel.bestCpi}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, bestCpi: parseFloat(e.target.value) || 0}))}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="percentMaleInCpi">% Male in CPI</Label>
                              <Input
                                id="percentMaleInCpi"
                                type="number"
                                min="0"
                                max="100"
                                value={newPromoLevel.percentMaleInCpi}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, percentMaleInCpi: parseInt(e.target.value) || 0}))}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="bestCpt">Best CPT</Label>
                              <Input
                                id="bestCpt"
                                type="number"
                                step="0.01"
                                value={newPromoLevel.bestCpt}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, bestCpt: parseFloat(e.target.value) || 0}))}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="percentMaleInCpt">% Male in CPT</Label>
                              <Input
                                id="percentMaleInCpt"
                                type="number"
                                min="0"
                                max="100"
                                value={newPromoLevel.percentMaleInCpt}
                                onChange={(e) => setNewPromoLevel(prev => ({...prev, percentMaleInCpt: parseInt(e.target.value) || 0}))}
                              />
                            </div>
                          </div>

                          {/* Promo Links Arrays */}
                          <div className="space-y-4">
                            {/* Promos Tested */}
                            <div className="space-y-2">
                              <Label>Promos Tested</Label>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Enter promo tested link"
                                  value={currentPromoTestedLink}
                                  onChange={(e) => setCurrentPromoTestedLink(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddPromoLink('promosTested', currentPromoTestedLink);
                                      setCurrentPromoTestedLink('');
                                    }
                                  }}
                                />
                                <Button 
                                  onClick={() => {
                                    handleAddPromoLink('promosTested', currentPromoTestedLink);
                                    setCurrentPromoTestedLink('');
                                  }}
                                  disabled={!currentPromoTestedLink.trim()}
                                  size="sm"
                                >
                                  Add
                                </Button>
                              </div>
                              {newPromoLevel.promosTested.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {newPromoLevel.promosTested.map((link, index) => (
                                    <Badge key={index} variant="outline" className="bg-blue-50 text-blue-800 pr-1">
                                      <span className="max-w-[100px] truncate" title={link}>
                                        {link.length > 15 ? `${link.substring(0, 15)}...` : link}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemovePromoLink('promosTested', index)}
                                        className="h-4 w-4 p-0 ml-1"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Promos with CPI < 2 */}
                            <div className="space-y-2">
                              <Label>Promos with CPI &lt; 2</Label>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Enter promo link with CPI < 2"
                                  value={currentCpiLink}
                                  onChange={(e) => setCurrentCpiLink(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddPromoLink('promosWithCpiLessThan2', currentCpiLink);
                                      setCurrentCpiLink('');
                                    }
                                  }}
                                />
                                <Button 
                                  onClick={() => {
                                    handleAddPromoLink('promosWithCpiLessThan2', currentCpiLink);
                                    setCurrentCpiLink('');
                                  }}
                                  disabled={!currentCpiLink.trim()}
                                  size="sm"
                                >
                                  Add
                                </Button>
                              </div>
                              {newPromoLevel.promosWithCpiLessThan2.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {newPromoLevel.promosWithCpiLessThan2.map((link, index) => (
                                    <Badge key={index} variant="outline" className="bg-green-50 text-green-800 pr-1">
                                      <span className="max-w-[100px] truncate" title={link}>
                                        {link.length > 15 ? `${link.substring(0, 15)}...` : link}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemovePromoLink('promosWithCpiLessThan2', index)}
                                        className="h-4 w-4 p-0 ml-1"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Promos with CPT < 50 */}
                            <div className="space-y-2">
                              <Label>Promos with CPT &lt; 50</Label>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Enter promo link with CPT < 50"
                                  value={currentCptLink}
                                  onChange={(e) => setCurrentCptLink(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddPromoLink('promosWithCptLessThan50', currentCptLink);
                                      setCurrentCptLink('');
                                    }
                                  }}
                                />
                                <Button 
                                  onClick={() => {
                                    handleAddPromoLink('promosWithCptLessThan50', currentCptLink);
                                    setCurrentCptLink('');
                                  }}
                                  disabled={!currentCptLink.trim()}
                                  size="sm"
                                >
                                  Add
                                </Button>
                              </div>
                              {newPromoLevel.promosWithCptLessThan50.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {newPromoLevel.promosWithCptLessThan50.map((link, index) => (
                                    <Badge key={index} variant="outline" className="bg-purple-50 text-purple-800 pr-1">
                                      <span className="max-w-[100px] truncate" title={link}>
                                        {link.length > 15 ? `${link.substring(0, 15)}...` : link}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemovePromoLink('promosWithCptLessThan50', index)}
                                        className="h-4 w-4 p-0 ml-1"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Add/Update Button */}
                          <div className="flex gap-2">
                            <Button 
                              onClick={handleAddPromoLevel}
                              disabled={!newPromoLevel.level.trim() || !newPromoLevel.writerName.trim()}
                            >
                              {isEditingPromo ? 'Update Promo' : 'Add Promo'}
                            </Button>
                            {isEditingPromo && (
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setIsEditingPromo(false);
                                  setEditingPromoId(null);
                                  setNewPromoLevel({
                                    level: '',
                                    writerName: '',
                                    producerName: '',
                                    promosTested: [],
                                    bestCpi: 0,
                                    percentMaleInCpi: 0,
                                    bestCpt: 0,
                                    percentMaleInCpt: 0,
                                    promosWithCpiLessThan2: [],
                                    promosWithCptLessThan50: [],
                                    deleted: false,
                                  });
                                }}
                              >
                                Cancel Edit
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Existing Promos List */}
                        {(formData.promos || []).filter(p => !p.deleted).length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-medium text-sm">Existing Promo Levels</h4>
                            {(formData.promos || [])
                              .filter(p => !p.deleted)
                              .map((promo, index) => (
                                <Card key={promo.id || index} className="p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div>
                                      <h5 className="font-medium">{promo.level}</h5>
                                      <p className="text-sm text-muted-foreground">
                                        Writer: {promo.writerName}
                                        {promo.producerName && ` • Producer: ${promo.producerName}`}
                                      </p>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditPromo(promo)}
                                        className="h-8 w-8 p-0"
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeletePromo(promo.id, index)}
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  <div className="text-xs space-y-2">
                                    <div className="grid grid-cols-2 gap-4">
                                      <span>CPI: {promo.bestCpi} ({promo.percentMaleInCpi}% male)</span>
                                      <span>CPT: {promo.bestCpt} ({promo.percentMaleInCpt}% male)</span>
                                    </div>
                                    
                                    {promo.promosTested.length > 0 && (
                                      <div>
                                        <span className="font-medium">Tested:</span> {promo.promosTested.length} promos
                                      </div>
                                    )}
                                    
                                    {promo.promosWithCpiLessThan2.length > 0 && (
                                      <div>
                                        <span className="font-medium">CPI &lt; 2:</span> {promo.promosWithCpiLessThan2.length} promos
                                      </div>
                                    )}
                                    
                                    {promo.promosWithCptLessThan50.length > 0 && (
                                      <div>
                                        <span className="font-medium">CPT &lt; 50:</span> {promo.promosWithCptLessThan50.length} promos
                                      </div>
                                    )}
                                  </div>
                                </Card>
                              ))}
                          </div>
                        )}

                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                </Accordion>
              </CardContent>
            </Card>

            {/* Add Show Button for Create Mode */}
            {!show && (
              <div className="mt-6 p-4 border-t bg-muted/30 rounded-lg">
                <Button 
                  onClick={handleSave} 
                  disabled={loading || !formData.ogShowName?.trim()}
                  size="lg"
                  className="w-full"
                >
                  Add Show
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t p-6 flex-shrink-0 bg-background">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {loading ? 'Saving changes...' : 
                 isAutoSaving ? 'Saving metadata...' : 
                 show ? 'Changes saved automatically' : 'Ready to create show'}
              </div>
              <Button variant="outline" onClick={onClose} disabled={loading}>
                {show ? 'Close' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}